// src/pages/mypage/MyPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import ReusableCalendar, {
  buildStatusCounts,
  getKRHolidayMap,
} from "../../components/common/feature/Calendar";
import { format } from "date-fns";
import { scheduleApi } from "../../api/schedules";
import BoardTable from "../../components/common/board/BoardTable";
import SidePanel from "../../components/common/layout/SidePanel";
import ScheduleNew from "../communication/schedule/ScheduleNew";
import ApprovalWidget from "../../components/widgets/ApprovalWidget";
import TodoWidget from "../../components/widgets/TodoWidget";
import PageHeader from "../../components/common/ui/PageHeader";

export default function MyPage() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [panelOpen, setPanelOpen] = useState(false);
  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchPersonalSchedules = useCallback(async () => {
    try {
      const res = await scheduleApi.listPersonal();
      setEvents(res.data?.results ?? res.data ?? []);
    } catch (err) {
      console.error("❌ personal 일정 로드 실패", err);
      setEvents([]);
    }
  }, []);

  useEffect(() => {
    fetchPersonalSchedules();
  }, [fetchPersonalSchedules]);

  const list = useMemo(
    () => events.filter((e) => e.start?.slice(0, 10) === dateStr),
    [events, dateStr]
  );

  // 🌟 점(dot) 표시를 위해 상태별 카운트로 변경
  const statusCountsByDate = useMemo(
    () =>
      buildStatusCounts(
        events,
        (m) => m.start,
        (m) =>
          m.scope === "personal"
            ? "green"
            : m.scope === "company"
              ? "red"
              : "blue"
      ),
    [events]
  );

  const [holidayMap, setHolidayMap] = useState({});
  useEffect(() => {
    (async () =>
      setHolidayMap(await getKRHolidayMap(selectedDate.getFullYear())))();
  }, [selectedDate]);

  return (
    <div className="p-6 space-y-8">
      <div>
        <PageHeader>My Page</PageHeader>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1.4fr,1fr,1fr] gap-8 items-start">
        <div className="space-y-5">
          {/* 캘린더 */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <ReusableCalendar
              value={selectedDate}
              onChange={setSelectedDate}
              statusCountsByDate={statusCountsByDate} // ⬅ dot 적용
              holidayMap={holidayMap}
              showCounts={true}
              showHolidayLabels={true}
            />
          </div>

          {/* 일정 리스트 */}
          <div className="page-box">

            {/* 헤더 */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-subtitle ml-2">{dateStr} 개인 일정</h3>

              <button
                className="btn-create"
                onClick={() => setPanelOpen(true)}
              >
                일정 등록
              </button>
            </div>

            {/* 테이블 */}
            <BoardTable
              keyField="id"
              rows={list}
              columns={[
                {
                  key: "time",
                  header: "시간",
                  render: (row) => format(new Date(row.start), "HH:mm"),
                },
                { key: "title", header: "제목" },
              ]}
              emptyText="오늘은 일정이 없습니다."
            />
          </div>

        </div>

        {/* Widgets */}
        <div className="space-y-5 sticky top-10">
          <div className="bg-white  overflow-hidden">
            <ApprovalWidget />
          </div>
          <div className=" bg-white  overflow-hidden">
            <TodoWidget />
          </div>
        </div>
      </div>

      <SidePanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        width={800}
      >
        <ScheduleNew
          scheduleType="personal"
          onSaved={() => {
            fetchPersonalSchedules();
            setPanelOpen(false);
          }}
          onClose={() => setPanelOpen(false)}
        />
      </SidePanel>
    </div>
  );
}
